import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";

import api from "@/app/lib/api/api";
import { showSnackbar } from "@/app/lib/context/SnackbarContext";
import { useCheckoutPayment } from "@/app/lib/hooks/useCheckoutPayment";
import {
  PurchaseStatus,
  type PaymentMethod,
  type PaymentResult,
} from "@/app/lib/payments/types";
import { platformApi } from "@/app/lib/platform";
import { HotelTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import type { TemporallyStoreHotelBookingDetailsBodyInputDto } from "@/app/lib/types/codegen";
import { updatePersonalData } from "@/app/lib/utils/bookings";

type PaymentParams = Pick<
  TemporallyStoreHotelBookingDetailsBodyInputDto,
  "rooms" | "availabilityToken" | "searchResultKey" | "searchResultToken"
> & {
  offerId: string;
  hotelId: string;
  checkInDate: Date;
};

type BnplPaymentParams = PaymentParams & {
  sessionId: string;
  otp: string;
};

type Props = {
  onSuccess: (result: PaymentResult) => void;
  initialAvailabilityToken?: string | null;
  method?: PaymentMethod;
};

export const useHotelBooking = ({
  initialAvailabilityToken,
  method,
  onSuccess,
}: Props) => {
  const t = useTranslations();
  const [checkoutPurchasePending, setCheckoutPurchasePending] = useState(false);

  const { flowComponent, isLoadingForm } = useCheckoutPayment({
    availabilityToken: initialAvailabilityToken,
    serviceType: "hotel",
    enabled: method === "checkout",
    onSuccess,
    onSubmit: () => setCheckoutPurchasePending(true),
  });

  const handleError = useCallback(
    (error: unknown) => {
      console.error("Error in booking or payment process:", error);
      Sentry.captureException(error);
      showSnackbar({
        type: "error",
        content: t("hotels.failed"),
      });
    },
    [t],
  );

  const checkoutPayment = {
    submit: async ({ offerId, ...postData }: PaymentParams) => {
      try {
        if (!flowComponent) {
          return;
        }

        const wasSuccess = await updatePersonalData();
        if (!wasSuccess) {
          return {
            orderId: "",
            status: PurchaseStatus.CANCELLED,
          };
        }

        await api.post(`/hotel/${offerId}/book`, postData);
        flowComponent.submit();
      } catch (error) {
        handleError(error);
        setCheckoutPurchasePending(false);
      }
    },
    isLoadingCheckoutForm: isLoadingForm,
    isPending: checkoutPurchasePending,
  };

  const qiPayment = useMutation({
    mutationFn: async ({
      offerId,
      hotelId,
      rooms,
      availabilityToken,
      searchResultKey,
      searchResultToken,
      checkInDate,
    }: PaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(`/hotels/${offerId}/book`, {
        availabilityToken,
        searchResultKey,
        searchResultToken,
        checkInDate: dayjs(checkInDate).format("YYYY-MM-DD"),
        hotelId: parseInt(hotelId),
        rooms,
      });

      // Use standard payment flow
      return platformApi.getPaymentProvider("standard").initiatePayment({
        availabilityTokens: [availabilityToken],
        serviceType: "hotel",
        onSuccess: ({ orderId }) => {
          rudderAnalytics.track(HotelTrackingEvents.USER_SUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "Success",
            transaction_id: orderId,
          });
        },
        onFailure: ({ orderId }) => {
          rudderAnalytics.track(HotelTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "fail",
            transaction_id: orderId,
          });
        },
      });
    },
    onSuccess,
    onError: handleError,
  });

  const bnplPayment = useMutation({
    mutationFn: async ({
      offerId,
      hotelId,
      otp,
      sessionId,
      rooms,
      availabilityToken,
      searchResultKey,
      searchResultToken,
      checkInDate,
    }: BnplPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(`/hotels/${offerId}/book`, {
        availabilityToken,
        searchResultKey,
        searchResultToken,
        checkInDate: dayjs(checkInDate).format("YYYY-MM-DD"),
        hotelId: parseInt(hotelId),
        rooms,
      });

      return platformApi.getPaymentProvider("bnpl").initiatePayment({
        availabilityTokens: [availabilityToken],
        sessionId,
        otp,
      });
    },
    onSuccess,
    onError: handleError,
  });

  return {
    qiPayment,
    checkoutPayment,
    bnplPayment,
  };
};
